import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar, CheckCircle2, Users, TrendingUp, Lock } from 'lucide-react';
import { adminLogin } from '@/lib/api';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      await adminLogin(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left side - Hero Image */}
      <div 
        className="hidden md:block md:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(46, 92, 85, 0.3), rgba(46, 92, 85, 0.3)), url('https://customer-assets.emergentagent.com/job_revival-tracker/artifacts/ogqes5a1_EV1.jpg')`
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center text-white p-12">
          <div>
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Empowering Revival Meetings</h2>
            <p className="text-lg opacity-90">Simplified financial tracking for your ministry's mission</p>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold text-primary mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>India Saved Mission</h1>
            <p className="text-muted-foreground">Financial tracking for your mission</p>
          </div>

          <div className="space-y-4 py-6">
            <div className="flex items-start space-x-3">
              <CheckCircle2 className="h-6 w-6 text-primary mt-1" />
              <div>
                <h3 className="font-semibold text-foreground">Budget Approval Workflow</h3>
                <p className="text-sm text-muted-foreground">Submit budgets and get admin approval</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Calendar className="h-6 w-6 text-primary mt-1" />
              <div>
                <h3 className="font-semibold text-foreground">Event-Based Tracking</h3>
                <p className="text-sm text-muted-foreground">Organize expenses by events</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <TrendingUp className="h-6 w-6 text-primary mt-1" />
              <div>
                <h3 className="font-semibold text-foreground">Google Sheets Sync</h3>
                <p className="text-sm text-muted-foreground">Auto-sync to your spreadsheets</p>
              </div>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-4 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Lock className="h-5 w-5 text-primary" />
              <span className="font-semibold text-foreground">Team Login</span>
            </div>
            
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="yourname@indiasaved.org"
                className="mt-1"
                data-testid="email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="mt-1"
                data-testid="password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full py-5 text-base"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <p className="text-center text-xs text-muted-foreground">
            Access is restricted to authorized team members only
          </p>

          <div className="text-center mt-6 pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground italic mb-3">
              Pray - India Shall Be Saved
            </p>
            <a 
              href="https://churchmultiplied.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              <span className="opacity-60">Powered by</span>
              <span className="font-semibold">ChurchMultiplied.com</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
